// 사용자 관리 페이지 (관리자 전용)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  useToast,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Spinner,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { SearchIcon, EditIcon } from '@chakra-ui/icons';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import { getAllUsers, updateUserRole } from 'utils/firestoreUtils';

function UserManagement() {
  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    filterUsers();
  }, [users, roleFilter]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast({
        title: '검색어 입력',
        description: '이름, 이메일 또는 전화번호를 입력하세요.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      const data = await getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('사용자 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '사용자 목록을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Role 필터
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // 검색어 필터
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.phone?.toLowerCase().includes(term)
      );
    }

    setFilteredUsers(filtered);
  };

  const handleEditRole = (user) => {
    setEditingUser(user);
    setNewRole(user.role);
    onOpen();
  };

  const handleSaveRole = async () => {
    if (!newRole) {
      toast({
        title: '입력 오류',
        description: '역할을 선택해주세요.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    try {
      await updateUserRole(editingUser.id, newRole);
      // 역할 변경 후 다시 검색
      await handleSearch();
      onClose();
      toast({
        title: '수정 완료',
        description: `${editingUser.name}님의 역할이 ${getRoleName(newRole)}(으)로 변경되었습니다.`,
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('역할 변경 오류:', error);
      toast({
        title: '변경 실패',
        description: '역할 변경에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    }
  };

  const getRoleName = (role) => {
    const roleNames = {
      admin: '최고 관리자',
      manager: '관리자',
      candidate: '후보자'
    };
    return roleNames[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'red',
      manager: 'purple',
      candidate: 'blue'
    };
    return colors[role] || 'gray';
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      active: 'green',
      inactive: 'gray',
      suspended: 'red'
    };
    return colors[status] || 'gray';
  };

  const groupedUsers = {
    admin: filteredUsers.filter(u => u.role === 'admin'),
    manager: filteredUsers.filter(u => u.role === 'manager'),
    candidate: filteredUsers.filter(u => u.role === 'candidate')
  };

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 헤더 영역 */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={textColor} mb={1}>
            사용자 관리
          </Heading>
          {hasSearched && (
            <Text fontSize="sm" color="gray.500">
              전체: {users.length}명 | 관리자: {groupedUsers.admin.length + groupedUsers.manager.length}명 | 후보자: {groupedUsers.candidate.length}명
            </Text>
          )}
        </Box>
      </Flex>

      {/* 검색 및 필터 영역 */}
      <Card mb={6}>
        <CardBody py={4}>
          <Flex align="center" gap={3} flexWrap="wrap">
            <Select
              w="auto"
              minW="150px"
              maxW="200px"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">모든 역할</option>
              <option value="admin">최고 관리자</option>
              <option value="manager">관리자</option>
              <option value="candidate">후보자</option>
            </Select>

            <InputGroup w="auto" minW="250px" maxW="400px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="이름, 이메일, 전화번호 검색"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
              />
            </InputGroup>
            
            <Button
              colorScheme="blue"
              leftIcon={<SearchIcon />}
              onClick={handleSearch}
              isLoading={loading}
            >
              검색
            </Button>
          </Flex>
        </CardBody>
      </Card>

      {/* 테이블 영역 */}
      <Card>
        <CardBody>
          {/* 탭 */}
          <Tabs colorScheme="blue">
            <TabList>
              <Tab>전체 ({hasSearched ? filteredUsers.length : 0})</Tab>
              <Tab>관리자 ({hasSearched ? groupedUsers.admin.length + groupedUsers.manager.length : 0})</Tab>
              <Tab>후보자 ({hasSearched ? groupedUsers.candidate.length : 0})</Tab>
            </TabList>

            <TabPanels>
              {/* 전체 탭 */}
              <TabPanel px={0}>
                {loading ? (
                  <Flex justify="center" py={10}>
                    <Spinner size="xl" />
                  </Flex>
                ) : !hasSearched ? (
                  <Box textAlign="center" py={10}>
                    <SearchIcon boxSize={12} color="gray.300" mb={4} />
                    <Text color="gray.500" fontSize="lg" fontWeight="medium">
                      이름, 이메일 또는 전화번호로 검색하세요
                    </Text>
                    <Text fontSize="sm" color="gray.400" mt={2}>
                      검색 버튼을 클릭하거나 Enter 키를 누르세요
                    </Text>
                  </Box>
                ) : filteredUsers.length === 0 ? (
                  <Box textAlign="center" py={10}>
                    <Text color="gray.500">검색 결과가 없습니다.</Text>
                  </Box>
                ) : (
                  <UserTable
                    users={filteredUsers}
                    onEditRole={handleEditRole}
                    getRoleName={getRoleName}
                    getRoleBadgeColor={getRoleBadgeColor}
                    getStatusBadgeColor={getStatusBadgeColor}
                  />
                )}
              </TabPanel>

              {/* 관리자 탭 */}
              <TabPanel px={0}>
                {loading ? (
                  <Flex justify="center" py={10}>
                    <Spinner size="xl" />
                  </Flex>
                ) : !hasSearched ? (
                  <Box textAlign="center" py={10}>
                    <SearchIcon boxSize={12} color="gray.300" mb={4} />
                    <Text color="gray.500" fontSize="lg" fontWeight="medium">
                      이름, 이메일 또는 전화번호로 검색하세요
                    </Text>
                    <Text fontSize="sm" color="gray.400" mt={2}>
                      검색 버튼을 클릭하거나 Enter 키를 누르세요
                    </Text>
                  </Box>
                ) : groupedUsers.admin.length + groupedUsers.manager.length === 0 ? (
                  <Box textAlign="center" py={10}>
                    <Text color="gray.500">관리자가 없습니다.</Text>
                  </Box>
                ) : (
                  <UserTable
                    users={[...groupedUsers.admin, ...groupedUsers.manager]}
                    onEditRole={handleEditRole}
                    getRoleName={getRoleName}
                    getRoleBadgeColor={getRoleBadgeColor}
                    getStatusBadgeColor={getStatusBadgeColor}
                  />
                )}
              </TabPanel>

              {/* 후보자 탭 */}
              <TabPanel px={0}>
                {loading ? (
                  <Flex justify="center" py={10}>
                    <Spinner size="xl" />
                  </Flex>
                ) : !hasSearched ? (
                  <Box textAlign="center" py={10}>
                    <SearchIcon boxSize={12} color="gray.300" mb={4} />
                    <Text color="gray.500" fontSize="lg" fontWeight="medium">
                      이름, 이메일 또는 전화번호로 검색하세요
                    </Text>
                    <Text fontSize="sm" color="gray.400" mt={2}>
                      검색 버튼을 클릭하거나 Enter 키를 누르세요
                    </Text>
                  </Box>
                ) : groupedUsers.candidate.length === 0 ? (
                  <Box textAlign="center" py={10}>
                    <Text color="gray.500">후보자가 없습니다.</Text>
                  </Box>
                ) : (
                  <UserTable
                    users={groupedUsers.candidate}
                    onEditRole={handleEditRole}
                    getRoleName={getRoleName}
                    getRoleBadgeColor={getRoleBadgeColor}
                    getStatusBadgeColor={getStatusBadgeColor}
                  />
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>

      {/* 역할 변경 모달 */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>사용자 역할 변경</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box mb={4} p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="sm" color="gray.600">사용자 정보</Text>
              <Text fontWeight="bold">{editingUser?.name}</Text>
              <Text fontSize="sm" color="gray.600">{editingUser?.email}</Text>
              <Text fontSize="sm" color="gray.600">{editingUser?.phone}</Text>
            </Box>

            <FormControl>
              <FormLabel>역할 선택</FormLabel>
              <Select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
              >
                <option value="admin">최고 관리자 (admin)</option>
                <option value="manager">관리자 (manager)</option>
                <option value="candidate">후보자 (candidate)</option>
              </Select>
              <Text fontSize="xs" color="gray.500" mt={2}>
                <strong>권한 설명:</strong><br />
                • 최고 관리자: 모든 기능 접근 가능<br />
                • 관리자: 구인 공고, 스케줄 관리 등 주요 기능 접근<br />
                • 후보자: 공고 지원, 스케줄 신청만 가능
              </Text>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              취소
            </Button>
            <Button colorScheme="blue" onClick={handleSaveRole}>
              변경
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

// 사용자 테이블 컴포넌트
function UserTable({ users, onEditRole, getRoleName, getRoleBadgeColor, getStatusBadgeColor }) {
  return (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th>이름</Th>
          <Th>이메일</Th>
          <Th>전화번호</Th>
          <Th>역할</Th>
          <Th>상태</Th>
          <Th>가입일</Th>
          <Th>작업</Th>
        </Tr>
      </Thead>
      <Tbody>
        {users.map((user) => (
          <Tr key={user.id}>
            <Td fontWeight="bold">{user.name}</Td>
            <Td fontSize="sm">{user.email}</Td>
            <Td fontSize="sm">{user.phone || '-'}</Td>
            <Td>
              <Badge colorScheme={getRoleBadgeColor(user.role)}>
                {getRoleName(user.role)}
              </Badge>
            </Td>
            <Td>
              <Badge colorScheme={getStatusBadgeColor(user.status)}>
                {user.status || 'active'}
              </Badge>
            </Td>
            <Td fontSize="sm">
              {user.createdAt ? new Date(user.createdAt).toLocaleDateString('ko-KR') : '-'}
            </Td>
            <Td>
              <IconButton
                icon={<EditIcon />}
                size="sm"
                onClick={() => onEditRole(user)}
                aria-label="역할 변경"
                title="역할 변경"
              />
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

export default UserManagement;
